import { ObservabilityEvent } from '../../../types';

const TOOL_LABELS: Record<string, string> = {
  // Investigation: AWS execution
  use_aws_customer: 'Queried customer AWS account',
  use_aws_operations: 'Queried operations account',
  aws_specialist: 'Delegated AWS task',
  // Investigation: findings
  add_finding: 'Added finding',
  update_finding: 'Updated finding',
  delete_finding: 'Deleted finding',
  get_findings: 'Retrieved findings',
  // Investigation: knowledge items
  add_knowledge_item: 'Linked knowledge document',
  update_knowledge_item: 'Updated knowledge item',
  delete_knowledge_item: 'Deleted knowledge item',
  get_knowledge_items: 'Retrieved knowledge items',
  // Investigation: metadata + search
  update_investigation_metadata: 'Updated investigation',
  search_customer_investigations: 'Searched past investigations',
  complete_investigation: 'Completed investigation',
  search_public_web: 'Fetched web content',
  // Customer management
  get_customer: 'Retrieved customer details',
  get_customer_accounts: 'Listed AWS accounts',
  get_customer_config: 'Retrieved customer config',
  get_deployment: 'Retrieved deployment',
  get_deployments: 'Listed deployments',
  get_current_user: 'Retrieved current user',
  get_current_user_permissions: 'Retrieved permissions',
  // Knowledge base
  retrieve_cloud2_knowledge: 'Searched Cloud2 knowledge base',
  retrieve_customer_knowledge: 'Searched customer knowledge base',
  add_customer_knowledge_document: 'Uploaded knowledge document',
  // Event management
  create_opsitem: 'Created action log item',
  list_opsitems: 'Listed action log items',
  update_opsitem: 'Updated action log item',
  // Service catalog
  list_available_services: 'Listed available services',
  list_products: 'Listed catalog products',
  list_subscriptions: 'Listed subscriptions',
  subscribe_to_services: 'Subscribed to services',
  unsubscribe_from_services: 'Unsubscribed from services',
};

export function getToolLabel(toolName: string): string {
  return TOOL_LABELS[toolName] ?? toolName.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

export function getToolDisplay(toolName: string, contextSummary?: string): string {
  if (contextSummary) return contextSummary;
  return getToolLabel(toolName);
}

export function formatDuration(ms: number): string | null {
  if (ms < 1000) return null;
  const seconds = Math.round(ms / 1000);
  return `${seconds}s`;
}

export interface ToolSummary {
  tool_name: string;
  count: number;
  status: 'success' | 'error' | 'mixed' | 'pending';
  total_duration_ms?: number;
  context_summary?: string;
}

export interface ObservabilityGroup {
  reasoning: ObservabilityEvent | null;
  toolSummaries: ToolSummary[];
  totalToolCalls: number;
}

export function groupObservabilityEvents(events: ObservabilityEvent[]): ObservabilityGroup[] {
  const groups: ObservabilityGroup[] = [];
  let currentGroup: ObservabilityGroup = { reasoning: null, toolSummaries: [], totalToolCalls: 0 };
  let currentToolName: string | null = null;
  let currentToolSummary: ToolSummary | null = null;

  const pushCurrentTool = () => {
    if (currentToolSummary) {
      currentGroup.toolSummaries.push(currentToolSummary);
      currentToolSummary = null;
      currentToolName = null;
    }
  };

  for (const event of events) {
    if (event.event_type === 'thinking') {
      pushCurrentTool();
      if (currentGroup.reasoning || currentGroup.toolSummaries.length > 0) {
        groups.push(currentGroup);
      }
      currentGroup = { reasoning: event, toolSummaries: [], totalToolCalls: 0 };
    } else if (event.event_type === 'tool_start') {
      const toolName = event.tool_name || 'unknown';
      currentGroup.totalToolCalls++;

      if (toolName === currentToolName && currentToolSummary) {
        currentToolSummary.count++;
      } else {
        pushCurrentTool();
        currentToolName = toolName;
        currentToolSummary = {
          tool_name: toolName,
          count: 1,
          status: 'pending',
          total_duration_ms: undefined,
          context_summary: event.context_summary ?? undefined,
        };
      }
    } else if (event.event_type === 'tool_complete') {
      const toolName = event.tool_name || 'unknown';

      if (toolName === currentToolName && currentToolSummary) {
        if (event.duration_ms) {
          currentToolSummary.total_duration_ms = (currentToolSummary.total_duration_ms || 0) + event.duration_ms;
        }
        // The completion summary is the enriched one (e.g. account-prefixed for
        // use_aws_customer); prefer it over the account-less tool_start summary.
        if (event.context_summary) {
          currentToolSummary.context_summary = event.context_summary;
        }
        if (event.status === 'error') {
          currentToolSummary.status = currentToolSummary.status === 'success' ? 'mixed' : 'error';
        } else if (event.status === 'success') {
          if (currentToolSummary.status === 'error') {
            currentToolSummary.status = 'mixed';
          } else if (currentToolSummary.status === 'pending') {
            currentToolSummary.status = 'success';
          }
        }
      }
    }
  }

  pushCurrentTool();
  if (currentGroup.reasoning || currentGroup.toolSummaries.length > 0) {
    groups.push(currentGroup);
  }

  return groups;
}

export function getGroupSummary(group: ObservabilityGroup): string {
  if (group.toolSummaries.length === 0) {
    return 'Processing...';
  }

  // Build action-verb summary from context_summary fields where available,
  // falling back to TOOL_LABELS. For single tools, use a compact description.
  // For multiple distinct tools, combine up to two with " · ".
  const summaries = group.toolSummaries.map(t => {
    const display = t.context_summary ?? getToolLabel(t.tool_name);
    return t.count > 1 ? `${display} ×${t.count}` : display;
  });

  if (summaries.length === 1) {
    return summaries[0];
  }

  // Combine first two summaries; indicate remainder with count
  const rest = summaries.length - 2;
  const combined = summaries.slice(0, 2).join(' · ');
  return rest > 0 ? `${combined} +${rest} more` : combined;
}
